import { fetchWithAuth } from "@adafia/ui";
import type { FileNode } from "@/notes/types";
import { API_V1 } from "./config";

/**
 * Fetches Tree from the backend API.
 *
 * @returns A promise that resolves to FileNode[].
 * @throws An error if the fetch operation fails.
 */
export const fetchTree = async (): Promise<FileNode[]> => {
	const url = `${API_V1}/tree`;
	try {
		const response = await fetchWithAuth(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			const errorBody = await response.text();
			throw new Error(
				`API request failed with status ${response.status}: ${errorBody}`,
			);
		}

		const data = await response.json();

		if (data) {
			return data as FileNode[];
		}

		throw new Error("Invalid response format from server");
	} catch (error) {
		console.error("Error fetching tree:", error);
		throw new Error("Failed to fetch tree. Please try again later.");
	}
};

/**
 * Updates a FileNode on the backend API.
 *
 * @param id - The ID of the FileNode to be updated.
 * @param content - The new content of the note (optional).
 * @param title - The new title of the note (optional).
 * @returns A promise that resolves to the updated FileNode.
 * @throws An error if the update operation fails.
 */
export const updateFileNode = async (
	id: string,
	content?: string | null,
	title?: string,
	isPublished?: boolean,
): Promise<FileNode> => {
	const url = `${API_V1}/${id}`;
	try {
		const response = await fetchWithAuth(url, {
			method: "PATCH",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ content, title, isPublished }),
		});

		if (!response.ok) {
			const errorBody = await response.text();
			throw new Error(
				`API request failed with status ${response.status}: ${errorBody}`,
			);
		}

		const data = await response.json();

		if (data) {
			return data as FileNode;
		}

		throw new Error("Invalid response format from server");
	} catch (error) {
		console.error("Error updating file node:", error);
		throw new Error("Failed to update file node. Please try again later.");
	}
};

export const searchNotes = async (query: string): Promise<FileNode[]> => {
	const url = `${API_V1}/search?q=${encodeURIComponent(query)}`;
	try {
		const response = await fetchWithAuth(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			const errorBody = await response.text();
			throw new Error(
				`API request failed with status ${response.status}: ${errorBody}`,
			);
		}

		const data = await response.json();

		if (data) {
			return data as FileNode[];
		}

		throw new Error("Invalid response format from server");
	} catch (error) {
		console.error("Error searching notes:", error);
		throw new Error("Failed to search notes. Please try again later.");
	}
};

export const createNote = async (
	title: string,
	content: string | null,
	isFolder: boolean,
	parentId: string | null,
): Promise<FileNode> => {
	const url = `${API_V1}`;
	try {
		const response = await fetchWithAuth(url, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				title,
				content,
				isFolder,
				parentId,
			}),
		});

		if (!response.ok) {
			const errorBody = await response.text();
			throw new Error(
				`API request failed with status ${response.status}: ${errorBody}`,
			);
		}

		const data = await response.json();

		if (data) {
			return data as FileNode;
		}

		throw new Error("Invalid response format from server");
	} catch (error) {
		console.error("Error creating note:", error);
		throw new Error("Failed to create note. Please try again later.");
	}
};

export const deleteNote = async (id: string): Promise<void> => {
	const url = `${API_V1}/${id}`;
	try {
		const response = await fetchWithAuth(url, {
			method: "DELETE",
			headers: {
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			const errorBody = await response.text();
			throw new Error(
				`API request failed with status ${response.status}: ${errorBody}`,
			);
		}

		return;
	} catch (error) {
		console.error("Error deleting note:", error);
		throw new Error("Failed to delete note. Please try again later.");
	}
};

export const getNoteById = async (id: string): Promise<FileNode> => {
	const url = `${API_V1}/${id}`;
	try {
		const response = await fetchWithAuth(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			const errorBody = await response.text();
			throw new Error(
				`API request failed with status ${response.status}: ${errorBody}`,
			);
		}

		const data = await response.json();

		if (data) {
			return data as FileNode;
		}

		throw new Error("Invalid response format from server");
	} catch (error) {
		console.error("Error fetching note by ID:", error);
		throw new Error("Failed to fetch note. Please try again later.");
	}
};

export const fetchDetailedTree = async (): Promise<FileNode[]> => {
	const url = `${API_V1}/tree/detailed`;
	try {
		const response = await fetchWithAuth(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
		});
		if (!response.ok) {
			const errorBody = await response.text();
			throw new Error(
				`API request failed with status ${response.status}: ${errorBody}`,
			);
		}

		const data = await response.json();

		if (data) {
			return data as FileNode[];
		}

		throw new Error("Invalid response format from server");
	} catch (error) {
		console.error("Error fetching detailed tree:", error);
		throw new Error("Failed to fetch detailed tree. Please try again later.");
	}
};

export const moveNode = async (
	id: string,
	newParentId: string | null,
): Promise<FileNode> => {
	const url = `${API_V1}/${id}/move`;
	try {
		const response = await fetchWithAuth(url, {
			method: "PATCH",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ parentId: newParentId }),
		});

		if (!response.ok) {
			const errorBody = await response.text();
			throw new Error(
				`API request failed with status ${response.status}: ${errorBody}`,
			);
		}

		const data = await response.json();

		if (data) {
			return data as FileNode;
		}

		throw new Error("Invalid response format from server");
	} catch (error) {
		console.error("Error moving file node:", error);
		throw new Error("Failed to move file node. Please try again later.");
	}
};

export const fetchSearchNotesHighlight = async (
	query: string,
): Promise<FileNode[]> => {
	const url = `${API_V1}/search/highlight?q=${encodeURIComponent(query)}`;
	try {
		const response = await fetchWithAuth(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			const errorBody = await response.text();
			throw new Error(
				`API request failed with status ${response.status}: ${errorBody}`,
			);
		}

		const data = await response.json();

		if (data) {
			return data as FileNode[];
		}

		throw new Error("Invalid response format from server");
	} catch (error) {
		console.error("Error searching notes with highlight:", error);
		throw new Error("Failed to search notes. Please try again later.");
	}
};

export const uploadImage = async (file: File): Promise<{ url: string }> => {
	const url = `${API_V1}/images`;
	try {
		const formData = new FormData();
		formData.append("file", file);

		const response = await fetchWithAuth(url, {
			method: "POST",
			body: formData,
		});

		if (!response.ok) {
			const errorBody = await response.text();
			throw new Error(
				`API request failed with status ${response.status}: ${errorBody}`,
			);
		}

		const data = await response.json();
		if (data && typeof data.url === "string") {
			return data as { url: string };
		}

		throw new Error("Invalid response format from server");
	} catch (error) {
		console.error("Error uploading image:", error);
		throw new Error("Failed to upload image. Please try again.");
	}
};
